/**
 * Main menu component.
 * Displays the application's main navigation menu with collapsible content,
 * login section for anonymous users, and top bar. Handles menu visibility based on
 * current page and focus state, with animation transitions.
 */
import Content from '@components/elements/menu/menuContent';
import Context from '@shared/components/utils/context/menuContext';
import Focus from '@shared/types/enums/focus';
import Login from '@components/elements/menu/menuLogin';
import Mask from '@components/atoms/mask/menuMask';
import Pages from '@pages';
import Search from '@components/elements/menu/menuSearch';
import Container from '@components/atoms/container/menuContainer';
import { focusSelector, setFocus } from '@shared/store/slices/focus.slice';
import { modalSelector } from '@shared/store/slices/modal.slice';
import { routerSelector } from '@shared/store/slices/router.slice';
import { useAppDispatch, useAppSelector } from '@shared/store/hooks';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DURATION, TRANSITION } from '@shared/types/constants/animations';
import { MENU_PAGES_BLACKLIST } from '@shared/types/constants/pageVisibility';
import {
  MENU_FOCUS_BLACKLIST,
  MENU_FOCUS_WHITELIST,
} from '@shared/types/constants/focusVisibility';

/**
 * Main menu component that manages menu visibility and animations.
 * Displays menu content, login section, and handles focus-based animations.
 *
 * @returns {JSX.Element | null} Menu component or null if not initialized
 */
export default function Menu({ children }: { children: React.ReactNode }) {
  const { callbacks, context, props } = useMenu();
  return (
    <Context.Provider value={context}>
      <Mask {...props.mask} />
      <Container {...props.container} {...callbacks.container}>
        <Login />
        <Search />
        <Content />
      </Container>
      {children}
    </Context.Provider>
  );
}

/**
 * Hook to manage menu display and animation.
 * @returns {Object} Menu display, animation, data, and index
 */
function useMenu() {
  // ------------------------------------------------------------
  // Variables
  // ------------------------------------------------------------

  // Dispatch
  const dispatch = useAppDispatch();

  // Selectors
  const { focus, stack } = useAppSelector(focusSelector);
  const { current: modal } = useAppSelector(modalSelector);
  const {
    route: { Page },
  } = useAppSelector(routerSelector);

  // State
  const [collapse, setCollapse] = useState(false);
  const [display, setDisplay] = useState(false);

  // Refs
  const effectiveIndex = useRef(0);
  const listSelected = useRef(true);
  const setListIndex = useRef<(newIndex: number, increment?: number) => void>(
    () => null
  );

  // ------------------------------------------------------------
  // Event handlers
  // ------------------------------------------------------------

  const onMouseEnter = useCallback(() => {
    if (!display) return;
    if (Page === Pages.SEARCH) dispatch(setFocus(Focus.SEARCH));
    else dispatch(setFocus(Focus.MENU));
  }, [display, Page]);

  // ------------------------------------------------------------
  // Effect hooks
  // ------------------------------------------------------------

  /**
   * Collapses or expands the menu based on the current focus.
   */
  useEffect(() => {
    // If modal is open, do not collapse the menu
    if (modal) return;

    // If focus is on ads, modal buttons, or splash, do not interact with the menu
    if (MENU_FOCUS_BLACKLIST.includes(focus as number)) return;

    // Display menu content
    setCollapse(!MENU_FOCUS_WHITELIST.includes(focus as number));

    // Set list selection to true
    listSelected.current = focus === Focus.MENU;
  }, [focus, modal, stack]);

  /**
   * Calculates whether the menu should be displayed based on the current page.
   */
  useEffect(() => {
    setDisplay(!MENU_PAGES_BLACKLIST.includes(Page));
  }, [Page]);

  // ------------------------------------------------------------
  // Memoized values
  // ------------------------------------------------------------

  return useMemo(() => {
    return {
      display,
      callbacks: {
        container: {
          onMouseEnter,
        },
      },
      context: {
        collapse,
        display,
        effectiveIndex,
        listSelected,
        setListIndex,
      },
      props: {
        container: {
          collapse,
          duration: DURATION.MENU_CONTAINER,
          display,
          transition: TRANSITION.MENU_CONTAINER(collapse),
        },
        mask: {
          display: !collapse && display,
        },
      },
    };
  }, [collapse, display, setListIndex, onMouseEnter]);
}
